/*
  PRODUCT-CARD IMAGE CAROUSEL (2026-09-21, Rishi: "change the collection grid images to a carousel … a chevron
  for the left and right controls on desktop and touch for the mobile version"; then: no position indicator,
  all photos, every product grid). A position line was added 2026-09-24 — see 5.
  REVISED 2026-09-22 (Rishi: "have the first and second image load in the carousel on page load but have the
  other images for the product load in sequence when a user interacts with the carousel. Also, I would like the
  carousel to go back to the first image at the end to create an infinite scroll.")

  Markup: snippets/product-card.liquid renders every photo of a product inside `.r-card-track`, a native
  horizontal scroller with scroll-snap, so a phone swipes it with no script at all. This file adds:

  1. PHOTO 2 LOADS WITH THE CARD. It arrives with `data-r-early` and no URL, and is filled in as soon as its card
     comes within 200px of the screen — so photos 1 and 2 arrive together, for the cards on screen at page load
     and for every card further down as it is scrolled to. Not a native `loading="lazy"`: a lazy image sitting
     off to the side inside a scroller is clipped by it, and Safari/Firefox then never fetch it until it is
     swiped into view — the blank second photo this replaces. Not eager on every card either: that would fetch
     a second photo for all 48 cards up front, doubling the grid's image weight for cards nobody reaches.
  2. PHOTOS 3+ LOAD ONE AFTER ANOTHER, IN ORDER, from the first sign of interest in that card — the photos
     scroll (a swipe, a trackpad, a chevron click) or the pointer rests on a chevron. NOT on merely touching or
     passing over a card: on a phone every page scroll starts on a photo (review 2026-09-21). Each photo waits
     for the one before it, so a card never opens eight downloads at once; a photo the shopper jumps to ahead of
     the queue is fetched straight away and the queue then skips it.
     Every deferred photo is sized to the card's MEASURED width: the grid's `sizes` formula over-asks by up to
     2× on collection pages and falls back to 100vw in predictive search.
  3. IT LOOPS. After the last photo the carousel carries on to photo 1 in the same direction. A copy of photo 1
     (`.r-card-clone`, made the first time the card is used — on a touch screen, the first time a finger touches it:
     see 5) sits after the last photo; when a move comes to
     rest on it, the track jumps invisibly to the real photo 1. That works for a swipe as well as a chevron.
     Going back from photo 1 jumps invisibly onto the copy first and then eases back to the last photo, so the
     wrap looks the same on the chevrons in both directions. A SWIPE loops forwards only — swiping back from
     photo 1 stops there, as it always has. The copy shows the exact file photo 1 already downloaded (its
     `currentSrc`, no srcset), so nothing new is fetched.
  4. THE CHEVRONS (desktop only; CSS hides them on touch screens). Click = one photo along, never a navigation
     to the product. With the loop there is no end, so neither chevron is ever disabled. A mousedown on a
     chevron does not take focus (it is aria-hidden). Reduced-motion users get an instant jump.
  5. THE PROGRESS LINE (2026-09-24, Rishi: "a thin black line at the very bottom of the image that grows as the
     image carousel is swiped or clicked. The line should not be visible before the carousel is swiped or
     clicked … should reach the end of the image when the last image is visible … should restart from the
     beginning when the first image shows again"). Photo k of n draws k/n of the width. The line is created the
     first time a card shows a photo OTHER than photo 1 — a chevron click, or a swipe that really changes the
     photo — so a page scroll that nudges a card sideways and snaps back leaves nothing behind. From then on it
     stays, at 1/n on photo 1. The copy of photo 1 counts as photo 1, and arriving on it (the loop carrying on
     past the last photo) SNAPS the line back to its first step instead of sliding it back across the image.
     Styled in r-custom.css ("THE PROGRESS LINE").
     ⚠️ ON A TOUCH SCREEN THE LINE AND THE LOOP COPY ARE MADE WHEN A FINGER FIRST TOUCHES THE CARD, before its photos
     move (2026-10-07, Delinda via Rishi: the first swipe on a collection card stopped short, as the product page's
     gallery had that afternoon). Both used to be added by the first scroll event of the first swipe, and on iPhone
     Safari an element added while a swipe is under way (with the layout it forces) cancels the swipe's glide and its
     snap, so the first flick on every card stopped between photos; later swipes, with both already there, were fine.
     The line is made hidden (`--idle`) at nothing and is still first SHOWN on the first real photo change, so the rule
     above holds. Nothing is fetched: the copy shows photo 1's own file. A mouse still makes them on first use.
     REVISED 2026-09-24 (Rishi: "The line should fade out after 3 seconds" · "Use a negative color"): it fades out
     3 s after the photos stop moving — never while a finger still rests on them — and the next move to another
     photo brings it back at the new position. Movement within the same photo keeps a showing line awake but
     does not wake a faded one, for the same reason the first reveal waits for a real photo change. The negative
     is pure CSS (white + mix-blend-mode: difference, as on the chevrons). MOVED TO THE TOP of the photo the same
     evening (Rishi: "move the line to the top of the image") — also CSS only.

  6. THE SWIPE HINT (2026-09-25, Rishi: "animate the first card in a collection on mobile in a way that shows the
     user that the image is swipable … when the page loads"). See hint() at the end. Since 2026-10-08 it plays on every
     page load, and random cards on screen peek as the shopper scrolls (6b, randomPeeks()).

  7. THE HOVER PEEK (2026-10-06, Rishi: "I would like to add mouseovers to the collection grids. I would like the second
     position image to show." He then chose "Hover = photo 2, arrows continue" and "Fade"). Desktop mouse only. Pointing
     at a card — its photo, name or price — fades the photo to photo 2; the chevrons carry on from there (next = photo 3,
     back = photo 1); moving off the card fades it back to photo 1, from wherever the chevrons left it. Phones untouched.
     - The fade is an overlay laid OVER the track (`.r-card-peek`) showing the exact file the track already holds, so
       nothing new is fetched. Once it is fully shown (its own transitionend; a timer only as a fallback), the track jumps
       underneath to that photo and the overlay is hidden in the same frame — the same picture, so nothing visibly changes,
       and from then on the carousel really is on that photo. A fade on the track itself was not possible: the track is a
       scroller, its photos sit side by side, and its scroll position is what the loop, the queue and the line all read.
     - ONE overlay per card, made on its first peek and then kept, hidden, in the link: on this site inserting or removing
       an element costs a style pass over the whole page (its :has() rules, ~10ms each, measured 2026-10-06); changing a
       class or a src does not.
     - The jumps are QUIET: they are not interest in the card (no loading queue, no loop copy) and draw no progress line, so
       sweeping the mouse over a grid fetches nothing beyond photo 2, which every card already loads, and leaves no lines.
       A line still showing from the chevrons fades out with the photo and sleeps at the new photo; the next move wakes it.
     - Coming back before a fade has finished undoes it: leaving while photo 2 is still fading in leaves photo 1 where it
       was; coming back while photo 1 is fading back in keeps the photo the chevrons had reached. A chevron move that is
       already heading to photo 1 when the pointer leaves is left to land, not cut short.
     - A chevron click during the fade-in finishes it first (photo 2 shows at once), so the move carries on from photo 2;
       so does a sideways trackpad move on the photos (not in a row that itself scrolls sideways: there the wheel moves the
       row and the fade simply carries on).
     - A photo that has not arrived yet is waited for, never faded to blank; if it fails, leaving still ends on photo 1.
     Revised the same day after a 20-agent review (verification + adversarial confirmation): the fade was clocked by a
     timer, the overlay was inserted and removed on every hover, the quiet jump had a 300ms window that a busy page could
     miss, leaving mid-move to photo 1 cut instead of landing, an unloaded photo 1 left the card on photo 2, the line could
     read the old photo, the hover zone was the photo only, and a sideways wheel in the Related row cut the fade.

  Event delegation on `document`, plus a MutationObserver for photo 2, so cards that arrive later — the next page
  of a collection, filters applied in place, recommendations — work with no re-initialising.
*/
(() => {
  const WIDTHS = [200, 300, 400, 500, 600, 700, 800, 900, 1000, 1200, 1400, 1600, 1800];
  const SCROLLEND = 'onscrollend' in window;
  const loads = new WeakMap(); // img → Promise that settles when that photo has loaded or failed
  const state = new WeakMap(); // track → { target, timer, touching, started, bar, shown, fade }
  const IDLE_MS = 3000; // the progress line fades this long after the last movement
  const IDLE = 'r-card-progress--idle';
  // The swipe hint (6): played once per PAGE LOAD since 2026-10-08 (it was once per visit, in sessionStorage). Random
  // peeks as the shopper scrolls: 6b.
  const HINT = 'r-card-track--hint';
  const touchScreen = window.matchMedia('(hover: none), (pointer: coarse)');
  let hintUsed = false;
  let shopperSwiped = false; // the shopper has swiped a card themselves on this page: the random peeks (6b) stop
  let hintCheck = null; // set while a hint is pending: lets the page watcher end it if its card is replaced
  let peekObserve = null; // set by 6b: lets the page watcher hand it cards that arrive later
  const peeked = new WeakSet(); // cards that have already peeked on this page (6 and 6b): each peeks at most once
  const hintDone = () => hintUsed;
  const spendHint = () => {
    hintUsed = true;
  };

  const reduceMotion = () => window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  const isTrack = (el) => el instanceof Element && el.classList.contains('r-card-track');
  const trackOf = (el) => el && el.closest('.product-card__figure')?.querySelector('.r-card-track');
  const slidesOf = (track) => [...track.children].filter((el) => el.tagName === 'IMG');
  const realCount = (track) => slidesOf(track).filter((el) => !el.classList.contains('r-card-clone')).length;
  const stateOf = (track) => {
    let s = state.get(track);
    if (!s) {
      s = { target: null, timer: 0, retry: 0, touching: false, swiped: false, moved: false, x0: 0, y0: 0,
        started: false, busyUntil: 0, bar: null, shown: 0, fade: 0,
        peekEl: null, peekTo: -1, peekGen: 0, peekTimer: 0, quietLeft: null, quietGen: 0 }; // 7: the hover peek
      state.set(track, s);
    }
    return s;
  };
  /* ⚠️ THE REAL, FRACTIONAL WIDTH — not `clientWidth`, which is rounded to a whole pixel. Each photo is 100% of the
     track, so photo k snaps at k × the real width; with a rounded width the error grows with k (a 300.4px card
     puts photo 8 3.2px off) and "has it arrived?" answered no forever (review 2026-09-22). */
  const slideWidth = (track) => track.getBoundingClientRect().width || 1;
  const indexOf = (track) => Math.round(track.scrollLeft / slideWidth(track));
  const restingOn = (track, index) => Math.abs(track.scrollLeft - index * slideWidth(track)) < 2;
  const jump = (track, index) => track.scrollTo({ left: index * slideWidth(track), behavior: 'instant' });

  /* Give a deferred photo its URL. Safe to call any number of times; the promise settles on load OR error, so
     one broken photo can never stall the queue behind it. */
  const load = (img) => {
    if (!img) return Promise.resolve();
    if (loads.has(img)) return loads.get(img);
    let done = Promise.resolve();
    if (img.dataset.rSrc) {
      const track = img.closest('.r-card-track');
      const cardWidth = Math.ceil((track || img).getBoundingClientRect().width);
      const max = Number(img.dataset.rW) || 1800;
      const at = (w) => {
        const u = new URL(img.dataset.rSrc, location.href);
        u.searchParams.set('width', w);
        return u.href;
      };
      const widths = WIDTHS.filter((w) => w <= max);
      if (!widths.length) widths.push(max);
      done = new Promise((resolve) => {
        img.addEventListener('load', resolve, { once: true });
        img.addEventListener('error', resolve, { once: true });
      });
      /* ⚠️ A hidden card measures 0 wide. Without a `sizes` the browser assumes 100vw and fetches a
         full-screen photo, so fall back to the grid's own `sizes` from photo 1. */
      const fallback = track?.firstElementChild?.getAttribute('sizes');
      if (cardWidth) img.sizes = `${cardWidth}px`;
      else if (fallback) img.sizes = fallback;
      img.srcset = widths.map((w) => `${at(w)} ${w}w`).join(', ');
      img.src = at(Math.min(800, max));
      img.removeAttribute('data-r-src');
    }
    loads.set(img, done);
    return done;
  };

  /* The copy of photo 1 that the loop lands on. Photo 2's classes, not photo 1's: photo 1 carries
     `product-card__image--primary`, which the theme uses for the card's main image.
     ⚠️ THE COPY SHOWS PHOTO 1'S EXACT FILE (`currentSrc`), WITH NO srcset OR sizes. A plain clone re-picked from
     the srcset: most cards' photo 1 is lazy with `sizes="auto, …"`, `auto` only counts on a lazy image, so an
     eager copy fell back to the grid formula and Chrome downloaded photo 1 again at up to twice the size (review
     2026-09-22). Eager, so Safari/Firefox do not leave the clipped copy blank; it comes from the cache. */
  const ensureClone = (track) => {
    if (track.querySelector(':scope > .r-card-clone')) return;
    const [first, second] = slidesOf(track);
    if (!first || !second) return;
    /* ⚠️ A NEW <img> with photo 1's attributes copied across, minus the ones that fetch — NOT `cloneNode`. A clone of photo 1
       carries its src and srcset, and Safari starts that download (photo 1 again, up to the 2000px file: 377KB measured)
       the moment the clone exists, before the attributes can be taken off (review 2026-10-06; in place since 2026-09-22). */
    const clone = document.createElement('img');
    for (const { name, value } of first.attributes) {
      if (!['id', 'fetchpriority', 'srcset', 'sizes', 'src', 'loading'].includes(name)) clone.setAttribute(name, value);
    }
    clone.className = `${second.className} r-card-clone`;
    clone.alt = '';
    clone.loading = 'eager';
    clone.setAttribute('aria-hidden', 'true');
    const fill = () => { clone.src = first.currentSrc || first.src; };
    if (first.complete && (first.currentSrc || first.src)) fill();
    else first.addEventListener('load', fill, { once: true });
    track.appendChild(clone);
  };

  /* First interest in a card: add the loop copy, then walk the photos in order, each waiting for the last. */
  const start = (track) => {
    if (!track) return;
    const s = stateOf(track);
    if (s.started) return;
    s.started = true;
    ensureClone(track);
    (async () => {
      for (const img of slidesOf(track)) {
        if (!img.classList.contains('r-card-clone')) await load(img);
      }
    })();
  };

  /* A move has come to rest. If it rests on the copy of photo 1, swap to the real photo 1 — the two are the same
     picture, so nothing visibly changes. Not while a finger is still on the photos — WHERE THERE IS NO `scrollend`.
     ⚠️ FIXED 2026-09-25 (Rishi: "please fix the 2 carousel bugs"): the finger rule is for browsers that guess "at
     rest" from a pause (a thumb held still mid-swipe is also a pause). Applied everywhere, it broke the loop in
     scrollend browsers: a thumb landing on the card while the snap onto the copy finished swallowed the only
     `scrollend`, and the card stayed parked on the copy, unable to loop forward. A real `scrollend` means the move
     has ended, and the swap is invisible, so it happens even under a finger. Trade-off, per the rule in the header
     that a swipe loops forwards only: a finger that then swipes BACK stops on photo 1 rather than the last photo.
     (Swapping on the lift instead was tried and rejected: a forward swipe's fling carried on from photo 1 and left
     the card stranded between photos — review 2026-09-24.) */
  const settle = (track) => {
    const s = stateOf(track);
    if (s.touching && !SCROLLEND) return;
    if (s.target !== null) {
      /* ⚠️ A chevron move can itself contain an instant jump (the wrap back from photo 1 lands on the copy
         first), and that jump reports "at rest" while the eased move after it is still running. Settling then
         would see the copy mid-move and yank the track to photo 1. So a move settles only once it has ARRIVED —
         or once it has had time to arrive and did not, meaning a trackpad took over and the rest is real. */
      if (!restingOn(track, s.target) && performance.now() < s.busyUntil) {
        // Declined while the move is live: look once more when it must be over, so nothing is left parked.
        clearTimeout(s.retry);
        s.retry = setTimeout(() => settle(track), s.busyUntil - performance.now() + 20);
        return;
      }
      s.target = null;
    }
    /* ⚠️ ONLY WHEN IT IS ACTUALLY RESTING ON THE COPY, not merely nearest to it. Rounding the position swapped
       mid-flick (a finger flicking back from the copy towards the last photo was sent to photo 1 instead) and
       under a Safari trackpad paused over the copy (review 2026-09-22). */
    const n = realCount(track);
    if (restingOn(track, n)) jump(track, 0);
  };

  /* Draw the progress line for the photo on show (5 in the header). During a chevron move that is the move's
     target, not the nearest photo — otherwise the line would step back and forth as the photo slides past the
     halfway mark. During a swipe it is the nearest photo. */
  /* Show the line and (re)start its fade clock. A finger still resting on the photos AFTER MOVING THEM holds it: the
     clock re-arms instead of fading, and release() restarts it when the finger lifts. Only a touch that moved the
     photos counts (`swiped`): a vertical page scroll, a long-press or a row swipe that merely STARTS on a photo held
     the line for up to 8.5 s (review 2026-09-24), so the line always fades 3 s after the
     lift (review 2026-09-24: without that it faded anywhere from 0 to 3 s after the lift). A card removed from the
     page (filters re-rendered under a resting finger, whose touchend then never reaches `document`) stops the clock
     instead of keeping the removed card alive forever (same review). */
  const wake = (track, s) => {
    s.bar.classList.remove(IDLE);
    clearTimeout(s.fade);
    const idle = () => {
      if (!s.bar.isConnected) return;
      if (s.touching && s.swiped) s.fade = setTimeout(idle, IDLE_MS);
      else s.bar.classList.add(IDLE);
    };
    s.fade = setTimeout(idle, IDLE_MS);
  };

  // The line itself, made hidden (asleep at nothing). See the ⚠️ in 5 for why a touch makes it ahead of the first swipe.
  const ensureBar = (track, s) => {
    if (s.bar) return s.bar;
    const figure = track.closest('.product-card__figure');
    if (!figure) return null;
    s.bar = document.createElement('span');
    s.bar.className = `r-card-progress ${IDLE}`;
    s.bar.setAttribute('aria-hidden', 'true');
    figure.appendChild(s.bar);
    return s.bar;
  };

  const progress = (track) => {
    const n = realCount(track);
    if (n < 2) return;
    const s = stateOf(track);
    const live = s.target !== null && performance.now() < s.busyUntil;
    const at = live ? s.target : indexOf(track);
    const wrapped = at >= n; // on the copy of photo 1
    const i = wrapped || at < 0 ? 0 : at;
    if (s.bar && i === s.shown) {
      // Still on the same photo: movement keeps a showing line awake, but never wakes a faded one.
      if (!s.bar.classList.contains(IDLE)) wake(track, s);
      return;
    }
    if (!s.bar) {
      // A chevron or a trackpad (a touch made the line already, hidden, before the photos moved).
      if (i === 0) return; // not moved off photo 1 yet: nothing to show
      if (!ensureBar(track, s)) return;
      s.bar.classList.remove(IDLE); // shows at once, as it always has for the chevrons
      s.bar.getBoundingClientRect(); // commit the empty line, so the first step grows from nothing
    }
    s.shown = i;
    const scale = `scaleX(${(i + 1) / n})`;
    if (wrapped) {
      /* ⚠️ Suppress only the WIDTH transition. `none` also cancelled a fade in progress, so a loop made during the
         fade dropped the line to nothing and faded it back in — a blink (review 2026-09-24). */
      s.bar.style.transition = 'opacity .4s ease';
      s.bar.style.transform = scale;
      s.bar.getBoundingClientRect(); // commit the snap before the width transition comes back
      s.bar.style.transition = '';
    } else {
      s.bar.style.transform = scale;
    }
    wake(track, s);
  };

  /* 7. THE HOVER PEEK (see the header). */
  const PEEK = 'r-card-peek';
  const PEEK_IN = 'r-card-peek--in';
  const PEEK_SNAP = 'r-card-peek--snap';
  const PEEK_MS = 250; // the fade; r-custom.css ("THE HOVER PEEK") runs the same .25s
  const mouseScreen = window.matchMedia('(hover: hover) and (pointer: fine)');

  // Settles once the photo has really arrived (or failed). load() alone settles at once for photo 1, which is never deferred.
  const arrived = (img) => (img.complete ? Promise.resolve() : new Promise((resolve) => {
    img.addEventListener('load', resolve, { once: true });
    img.addEventListener('error', resolve, { once: true });
  }));

  /* Jump the track without it counting as the shopper's move. The scroll listener swallows the ONE scroll event this jump
     causes, which is delivered with the next rendered frame; if none comes (the frame passed without one) the mark is
     dropped two frames later, so it can never swallow a real move. No clock: a busy page delays the frame and the mark
     together (a 300ms window let a stalled page count the jump as a move, review 2026-10-06). Already there: no jump. */
  const quietJump = (track, index) => {
    const s = stateOf(track);
    s.target = null;
    s.busyUntil = 0;
    if (restingOn(track, index)) return;
    s.quietLeft = index * slideWidth(track);
    const mark = ++s.quietGen;
    requestAnimationFrame(() => requestAnimationFrame(() => {
      if (s.quietGen === mark) s.quietLeft = null;
    }));
    jump(track, index);
  };

  // The progress line asleep (faded) at photo `index`, moved there without sliding across; the next real move wakes it.
  const sleepLine = (track, s, index) => {
    if (!s.bar) return;
    clearTimeout(s.fade);
    s.bar.classList.add(IDLE);
    s.bar.style.transition = 'opacity .4s ease';
    s.bar.style.transform = `scaleX(${(index + 1) / realCount(track)})`;
    s.bar.getBoundingClientRect();
    s.bar.style.transition = '';
    s.shown = index;
  };

  const hideOverlay = (el, instant) => {
    if (!el) return;
    if (instant) {
      el.classList.add(PEEK_SNAP); // no transition: gone in the same frame as the jump under it
      el.classList.remove(PEEK_IN);
      void getComputedStyle(el).opacity;
      el.classList.remove(PEEK_SNAP);
    } else {
      el.classList.remove(PEEK_IN); // fades out from wherever it had got to
    }
  };

  // The fade has finished (or a chevron / trackpad cut it short): the track takes over on the target photo.
  const finishPeek = (track, gen) => {
    const s = stateOf(track);
    if (s.peekTo < 0 || (gen !== undefined && gen !== s.peekGen)) return;
    clearTimeout(s.peekTimer);
    const to = s.peekTo;
    s.peekTo = -1;
    s.peekGen += 1;
    const el = s.peekEl;
    if (!el || !el.classList.contains(PEEK_IN)) return; // the photo had not arrived: nothing was shown, nothing to hand over
    quietJump(track, to);
    sleepLine(track, s, to);
    hideOverlay(el, true);
  };

  // One overlay per card, made on its first peek and kept (see the header). Photo 2's classes: the theme's card-image look
  // and the pale stage, as under it.
  const overlayOf = (track, s) => {
    if (s.peekEl && s.peekEl.isConnected) return s.peekEl;
    const el = document.createElement('img');
    const look = (slidesOf(track)[1] || slidesOf(track)[0]).className.replace(/\br-card-(slide|clone)\b/g, ' ');
    el.className = `${look} ${PEEK}`.replace(/\s+/g, ' ').trim();
    el.alt = '';
    el.setAttribute('aria-hidden', 'true');
    el.draggable = false;
    el.addEventListener('transitionend', (event) => {
      if (event.propertyName === 'opacity' && el.classList.contains(PEEK_IN)) finishPeek(track);
    });
    track.after(el);
    s.peekEl = el;
    return el;
  };

  // Undo a fade that has not finished. True if there was one.
  const cancelPeek = (track) => {
    const s = stateOf(track);
    if (s.peekTo < 0) return false;
    clearTimeout(s.peekTimer);
    s.peekTo = -1;
    s.peekGen += 1;
    hideOverlay(s.peekEl, false);
    return true;
  };

  const peek = (track, to) => {
    const s = stateOf(track);
    const img = slidesOf(track)[to];
    if (!img) return;
    const gen = ++s.peekGen;
    s.peekTo = to;
    // A line still showing from the chevrons fades with the photo instead of reading the old position over the new one.
    if (s.bar && !s.bar.classList.contains(IDLE)) {
      clearTimeout(s.fade);
      s.bar.classList.add(IDLE);
    }
    load(img).then(() => arrived(img)).then(() => {
      if (gen !== s.peekGen || !track.isConnected) return;
      if (!img.naturalWidth) { // failed: never fade to a blank. Leaving must still end on photo 1, so go there directly.
        s.peekTo = -1;
        if (to === 0) {
          quietJump(track, 0);
          sleepLine(track, s, 0);
        }
        return;
      }
      const el = overlayOf(track, s);
      const src = img.currentSrc || img.src;
      const show = () => {
        if (gen !== s.peekGen) return;
        el.classList.add(PEEK_IN);
        // transitionend hands over; this is the fallback when no transition runs (already opaque, a hidden tab)
        clearTimeout(s.peekTimer);
        s.peekTimer = setTimeout(() => finishPeek(track, gen), PEEK_MS + 150);
      };
      if (el.src !== src) {
        hideOverlay(el, true);
        el.src = src;
        // decoded before it fades in, so the fade never shows an empty frame
        (el.decode ? el.decode() : Promise.resolve()).catch(() => {}).then(show);
      } else {
        show(); // the same photo: a quick out-and-back carries on from the current opacity
      }
    });
  };

  // Which photo the card is on: during a chevron move, where it is going; the copy of photo 1 counts as photo 1.
  const photoOf = (track) => {
    const s = stateOf(track);
    const live = s.target !== null && performance.now() < s.busyUntil;
    const n = realCount(track);
    const at = live ? s.target : indexOf(track);
    return at >= n ? 0 : at;
  };

  /* The hover zone is the whole card — photo, name and price — as Rishi's choice put it ("Pointing at a card shows photo 2 …
     Moving the mouse off the card returns it to photo 1"). It was the photo alone, so reading the price brought photo 1
     back (review 2026-10-06). `pointerover` / `pointerout` bubble (enter/leave don't); moves inside the card are ignored. */
  const cardCrossed = (event) => {
    if (event.pointerType !== 'mouse' || !mouseScreen.matches) return null;
    const card = event.target.closest?.('.product-card');
    if (!card || card.contains(event.relatedTarget)) return null;
    const track = card.querySelector('.r-card-track');
    return track && realCount(track) > 1 ? track : null;
  };

  document.addEventListener('pointerover', (event) => {
    const track = cardCrossed(event);
    if (!track) return;
    const s = stateOf(track);
    if (s.peekTo === 1) return; // already on its way to photo 2 (a repeated event changes nothing)
    if (s.peekTo === 0) { // back before photo 1 had faded in: stay on the photo the chevrons reached
      cancelPeek(track);
      return;
    }
    if (photoOf(track) === 0) peek(track, 1);
  }, { passive: true });

  document.addEventListener('pointerout', (event) => {
    const track = cardCrossed(event);
    if (!track) return;
    const s = stateOf(track);
    if (s.peekTo === 0) return; // already on its way back
    if (s.peekTo === 1) { // left before photo 2 had faded in: photo 1 never went
      cancelPeek(track);
      return;
    }
    const live = s.target !== null && performance.now() < s.busyUntil;
    if (photoOf(track) !== 0) {
      peek(track, 0);
    } else if (live) {
      sleepLine(track, s, 0); // a chevron move already heading to photo 1 or its copy: let it land (settle swaps the copy)
    } else if (!restingOn(track, 0)) {
      quietJump(track, 0); // resting on the copy of photo 1, or a trackpad still moving off photo 1
      sleepLine(track, s, 0);
    }
  }, { passive: true });

  const go = (track, step) => {
    start(track);
    const n = realCount(track);
    if (n < 2) return;
    const s = stateOf(track);
    const hasClone = Boolean(track.querySelector(':scope > .r-card-clone'));
    /* ⚠️ A click during a move finishes that move instantly first, so fast clicking steps one photo per click
       instead of piling up relative scrolls that land between photos. The target is trusted only while its move
       is live: a trackpad that took over earlier leaves a stale one, and jumping to it flashed the wrong photo. */
    const live = s.target !== null && performance.now() < s.busyUntil;
    let from = live ? s.target : indexOf(track);
    if (live) jump(track, from);
    if (from >= n) {
      jump(track, 0);
      from = 0;
    }
    let to = from + step;
    if (to < 0) {
      if (hasClone) jump(track, n);
      to = n - 1;
    }
    s.target = to;
    s.busyUntil = performance.now() + 1000;
    progress(track);
    load(slidesOf(track)[to]);
    track.scrollTo({ left: to * slideWidth(track), behavior: reduceMotion() ? 'instant' : 'smooth' });
  };

  document.addEventListener('click', (event) => {
    const button = event.target.closest?.('.r-card-nav');
    if (!button) return;
    event.preventDefault();
    event.stopPropagation();
    const track = trackOf(button);
    if (!track) return;
    finishPeek(track); // a click during the hover fade-in carries on from photo 2 (7)
    go(track, button.classList.contains('r-card-nav--prev') ? -1 : 1);
  });

  // Keep focus where it was: the chevrons are aria-hidden, and a focused aria-hidden button is exposed anyway.
  document.addEventListener('mousedown', (event) => {
    if (event.target.closest?.('.r-card-nav')) event.preventDefault();
  });

  // Pointing at a chevron is the desktop signal of intent — start the queue before the click.
  document.addEventListener('pointerover', (event) => {
    const button = event.target.closest?.('.r-card-nav');
    if (button && event.pointerType === 'mouse') start(trackOf(button));
  }, { passive: true });

  // `scroll` and `scrollend` do not bubble, so listen in the capture phase.
  document.addEventListener('scroll', (event) => {
    const track = event.target;
    if (!isTrack(track)) return;
    // The hover peek's own jump (7) is not a move by the shopper: no loading queue, no loop copy, no progress line.
    const q = stateOf(track);
    if (q.quietLeft !== null) {
      const quiet = Math.abs(track.scrollLeft - q.quietLeft) < 2;
      q.quietLeft = null;
      if (quiet) return;
    }
    // Any real movement of the photos ends the swipe hint at once (the hint itself never scrolls the track).
    if (track.classList.contains(HINT)) track.classList.remove(HINT);
    // The progress line's hold counts only a touch whose finger moved SIDEWAYS (touchmove below): a page drag that
    // starts during the snap of the last swipe also sees track scroll events (review 2026-09-24).
    if (stateOf(track).touching && stateOf(track).moved) stateOf(track).swiped = true;
    start(track);
    // The photo sliding into view jumps the queue.
    load(slidesOf(track)[Math.min(Math.ceil(track.scrollLeft / slideWidth(track)), slidesOf(track).length - 1)]);
    progress(track);
    if (!SCROLLEND) {
      const s = stateOf(track);
      clearTimeout(s.timer);
      s.timer = setTimeout(() => settle(track), 150);
    }
  }, { passive: true, capture: true });

  if (SCROLLEND) {
    document.addEventListener('scrollend', (event) => {
      if (isTrack(event.target)) settle(event.target);
    }, { capture: true });
  }

  /* ⚠️ Without `scrollend` (older Safari) "at rest" is guessed from a pause in scroll events, and a finger held
     still mid-swipe is also a pause. Holding the swap until the finger lifts stops the track being yanked
     back to photo 1 from under a thumb that is still resting on the copy. */
  document.addEventListener('touchstart', (event) => {
    const track = event.target.closest?.('.r-card-track');
    if (!track) return;
    const s = stateOf(track);
    s.touching = true;
    s.swiped = false; // until this touch moves the photos (the scroll listener sets it)
    s.moved = false; // until the finger itself travels sideways (touchmove)
    s.x0 = event.touches[0]?.clientX ?? 0;
    s.y0 = event.touches[0]?.clientY ?? 0;
    s.target = null; // a finger takes over from any chevron move still in flight
    track.classList.remove(HINT); // a finger on the card ends the swipe hint at once
    // The loop copy and the hidden line, now — never mid-swipe (the ⚠️ in 5). Both are kept, so only a card's first touch adds them.
    if (realCount(track) > 1) {
      ensureClone(track);
      ensureBar(track, s);
    }
  }, { passive: true, capture: true });
  document.addEventListener('touchmove', (event) => {
    const track = event.target.closest?.('.r-card-track');
    if (!track) return;
    const s = stateOf(track);
    const t = event.touches[0];
    if (!s.touching || s.moved || !t) return;
    const dx = Math.abs(t.clientX - s.x0);
    if (dx > 8 && dx > Math.abs(t.clientY - s.y0)) {
      s.moved = true;
      spendHint(); // they have found the swipe themselves: no first-card hint on this page
      shopperSwiped = true; // … and no more random peeks (6b)
    }
  }, { passive: true, capture: true });
  const release = (event) => {
    const track = event.target.closest?.('.r-card-track');
    if (!track) return;
    const s = stateOf(track);
    s.touching = false;
    // A showing progress line fades 3 s after a lift that follows a swipe; a faded one stays faded (5 in the header).
    if (s.bar && s.swiped && !s.bar.classList.contains(IDLE)) wake(track, s);
    /* Where `scrollend` exists it arrives once the finger is up AND the snap has finished, so that is the only
       settle needed; a timer here fired mid-snap (review 2026-09-22). */
    if (SCROLLEND) return;
    clearTimeout(s.timer);
    s.timer = setTimeout(() => settle(track), 150);
  };
  document.addEventListener('touchend', release, { passive: true, capture: true });
  // A trackpad takes over from a chevron move the same way a finger does.
  document.addEventListener('wheel', (event) => {
    const track = event.target.closest?.('.r-card-track');
    if (!track || Math.abs(event.deltaX) <= Math.abs(event.deltaY)) return;
    // The trackpad carries on from photo 2 (7) — but not in a row that scrolls sideways (.scroll-area, track overflow
    // hidden), where the wheel moves the row and the card's fade should simply carry on.
    if (!track.closest('.scroll-area')) finishPeek(track);
    stateOf(track).target = null;
  }, { passive: true, capture: true });
  document.addEventListener('touchcancel', release, { passive: true, capture: true });

  // Photo 2 loads with its card: when the card comes within 200px of the screen.
  const early = 'IntersectionObserver' in window
    ? new IntersectionObserver((entries) => {
      for (const entry of entries) {
        if (!entry.isIntersecting) continue;
        early.unobserve(entry.target);
        load(entry.target.querySelector(':scope > img[data-r-early]'));
      }
    }, { rootMargin: '200px 0px' })
    : null;
  const watch = (root) => {
    const tracks = root.classList?.contains('r-card-track') ? [root] : root.getElementsByClassName?.('r-card-track') || [];
    for (const track of tracks) {
      if (track.dataset.rWatched) continue;
      track.dataset.rWatched = '1';
      if (early) early.observe(track);
      else load(track.querySelector(':scope > img[data-r-early]'));
    }
  };
  watch(document);

  /* 6. THE SWIPE HINT. Phones have no chevrons and no indicator, so the first card in a collection grid shows once
     that its photos move: they slide ~25% to the left, far enough to show the edge of photo 2, hold, and slide
     back (1.4 s; r-custom.css "THE SWIPE HINT").
     - A CSS animation on the photos' `transform`, NOT a scroll of the track: nothing here fires a scroll event, so
       the carousel's own machinery (photo loading queue, loop copy, settle, progress line) never runs for it.
     - Touch screens only (the devices without chevrons). Collection grid only (the main-collection section's
       product list — not product-page rows, not search). The FIRST card with more than one photo.
     - Plays once the card is ≥60% on screen AND its photo 2 has loaded (so the peek never shows a blank), 0.6 s
       later — and only if it is STILL on screen then, the page is visible and no finger is on the card; otherwise
       it waits for the next chance. Spent only when it actually plays. Photo 2 has 4 s to arrive, or no hint.
     - ON EVERY PAGE LOAD (2026-10-08, Rishi after speaking with Delinda: "make the peek happen on reload"; it was once per
       visit, in sessionStorage), and not after the shopper has swiped a card on this page. Skipped if the card was already
       used, cancelled the instant a finger touches it, skipped for reduced motion. */
  function hint() {
    if (!touchScreen.matches || reduceMotion() || hintDone() || !('IntersectionObserver' in window)) return;
    const track = [...document.querySelectorAll('.shopify-section--main-collection product-list .r-card-track')]
      .find((t) => realCount(t) > 1);
    if (!track) return;
    const second = slidesOf(track)[1];
    let visible = false; // the card is >= 60% on screen right now
    let ready = false; // photo 2 has loaded (never peek at a blank)
    let asked = false; // photo 2 requested
    let timer = 0;
    let ended = false; // finish() ran: nothing may restart the countdown (e.g. photo 2 arriving after a link tap)
    /* ⚠️ VISIBILITY IS CHECKED AT PLAY TIME, not only when first seen (verification 2026-09-25): a shopper who
       scrolled on during the wait made it play off screen and use up the visit's only hint unseen. So the observer
       stays connected, every move off/on screen cancels/restarts the 0.6 s countdown, and the hint is spent only
       when it actually plays. */
    // Nothing left to do on this page: stop the countdown, the observer and every listener.
    const finish = () => {
      ended = true;
      hintCheck = null;
      clearTimeout(timer);
      seen.disconnect();
      track.removeEventListener('touchend', again);
      track.removeEventListener('touchcancel', again);
      document.removeEventListener('click', onClick, true);
      removeEventListener('pagehide', finish);
    };
    const over = () => ended || hintDone() || !track.isConnected || stateOf(track).started;
    const arm = () => {
      clearTimeout(timer);
      if (over()) finish(); // spent elsewhere (a swipe on any card), the card was used, or it has gone
      else if (visible && ready) timer = setTimeout(play, 600);
    };
    // After a resting finger: a lift OR a cancel (a long-press that opened the link preview) restarts the countdown.
    const again = () => {
      track.removeEventListener('touchend', again);
      track.removeEventListener('touchcancel', again);
      arm();
    };
    /* ⚠️ A TAP ON ANY LINK ENDS THIS PAGE'S CHANCE, unspent (review 2026-09-25): tapping a card during the countdown
       (or the lift after a resting finger, which IS a tap) opened the product, and the hint then played during the
       navigation and used up the visit's only hint. The next page load gets it instead. */
    /* The countdown stops at the tap itself, so nothing can play between the tap and the navigation. Once the click has
       run its course: a link the theme handled in page (the Cart and Search icons open drawers: preventDefault), or
       one opening elsewhere, only paused it; a real navigation ends it (review 2026-09-25). */
    const onClick = (event) => {
      const a = event.target.closest?.('a[href]');
      if (!a) return;
      clearTimeout(timer);
      setTimeout(() => {
        if (event.defaultPrevented || a.target === '_blank' || event.metaKey || event.ctrlKey || event.shiftKey) arm();
        else finish();
      }, 0);
    };
    const play = () => {
      if (over()) {
        finish();
        return;
      }
      if (document.hidden) {
        document.addEventListener('visibilitychange', arm, { once: true });
        return;
      }
      // ⚠️ Never under a resting finger: a swipe started then would scroll photos that are still shifted (review
      // 2026-09-25). Try again 0.6 s after the finger leaves, if the card is still on screen and still unused.
      if (stateOf(track).touching) {
        track.addEventListener('touchend', again);
        track.addEventListener('touchcancel', again);
        return;
      }
      if (!visible) return; // waits for the next time the card is on screen
      /* ⚠️ NOTHING MAY COVER IT (review 2026-09-25): an IntersectionObserver ignores layers on top, so the hint
         played hidden behind an open Filters drawer and spent the visit's only hint unseen. If the card's centre
         is not the card, a drawer, menu or modal is over it: look again in 0.6 s. */
      const r = track.getBoundingClientRect();
      const top = document.elementFromPoint(r.left + r.width / 2, r.top + r.height / 2);
      if (!top || !track.contains(top)) {
        timer = setTimeout(play, 600);
        return;
      }
      spendHint();
      finish();
      peeked.add(track); // 6b never repeats it on this page
      track.classList.add(HINT);
      setTimeout(() => track.classList.remove(HINT), 1600);
    };
    const seen = new IntersectionObserver((entries) => {
      const e = entries[entries.length - 1];
      visible = e.isIntersecting && e.intersectionRatio >= 0.6;
      if (!visible) {
        clearTimeout(timer);
        if (over()) finish(); // e.g. the grid was re-rendered while the card was off screen
        return;
      }
      if (!asked) {
        asked = true;
        Promise.race([load(second), new Promise((r) => setTimeout(r, 4000))]).then(() => {
          ready = second.complete && second.naturalWidth > 0;
          if (ready) arm();
          else finish(); // photo 2 failed or took over 4 s: no hint on this page (never a blank peek)
        });
      } else {
        arm();
      }
    }, { threshold: 0.6 });
    seen.observe(track);
    document.addEventListener('click', onClick, true);
    addEventListener('pagehide', finish);
    // A grid re-rendered while the card is off screen gives the observer nothing to report, so the page watcher
    // below asks instead (review 2026-09-25: the old card, its observer and listeners otherwise stayed alive).
    hintCheck = () => {
      if (over()) finish();
    };
  }
  hint();

  /* 6b. RANDOM PEEKS AS THE SHOPPER SCROLLS (2026-10-08, Rishi after speaking with Delinda: "can the peek happen at random for
     visible products on the page as the user scrolls?"). The same peek as 6 (a CSS animation on the photos; nothing scrolls, so
     none of the carousel's machinery runs), on a card picked at random from those on screen:
     - WHILE THE PAGE SCROLLS SLOWLY (2026-10-08, Rishi: "do the peeks have to wait till a user stops scrolling?" → "yes, switch to
       that"): at most 5 times a second, if the page is moving at ≤ 0.35 px/ms (an unhurried browse, not a flick), a card that
       sits WHOLLY inside the middle 70% of the screen may peek, so it stays in view for the whole 1.4 s. A fast flick never
       peeks: the card would be gone before the peek ended and its one peek wasted.
     - AND WHEN THE PAGE COMES TO REST: 0.35 s after it stops, among cards ≥60% on screen.
     - Either way the card must have 2+ photos, already have photo 2 (never a blank peek: the grid loads photo 2 as a card
       nears the screen), still be on photo 1, be untouched and unused, and not have peeked yet on this page. Nothing may cover
       its centre (a drawer, the menu): if something does, another card is tried.
     - At most one peek every 3–6 s (a fresh random gap after each; if the page comes to rest sooner, it waits out the gap
       unless the shopper scrolls on), and every card peeks at most once per page load (the first card's hint, 6, counts).
     - Stops for the rest of the page once the shopper swipes a card themselves (they have found it). Touch screens only,
       never under reduced motion, collection grid only — as 6. A finger on a peeking card ends it at once (touchstart). */
  function randomPeeks() {
    if (!touchScreen.matches || reduceMotion() || !('IntersectionObserver' in window)) return;
    const GRID = '.shopify-section--main-collection product-list .r-card-track';
    const onScreen = new Set();
    const io = new IntersectionObserver((entries) => {
      for (const e of entries) {
        if (e.isIntersecting && e.intersectionRatio >= 0.6) onScreen.add(e.target);
        else onScreen.delete(e.target);
      }
    }, { threshold: [0, 0.6] });
    peekObserve = (root) => {
      const list = root.matches?.(GRID) ? [root] : root.querySelectorAll?.(GRID) || [];
      for (const t of list) {
        if (realCount(t) > 1) io.observe(t);
      }
    };
    peekObserve(document);
    const nextGap = () => 3000 + Math.random() * 3000;
    let lastAt = performance.now();
    let gap = nextGap();
    let rest = 0;
    const eligible = (t) => {
      if (!t.isConnected || peeked.has(t) || t.classList.contains(HINT)) return false;
      const s = stateOf(t);
      if (s.started || s.touching || !restingOn(t, 0)) return false;
      const second = slidesOf(t)[1];
      return Boolean(second && second.complete && second.naturalWidth > 0);
    };
    const SLOW = 0.35; // px per ms: faster than this is a flick, and nothing peeks mid-flick
    const BAND = 0.15; // the middle band leaves this fraction of the screen clear at the top and at the bottom
    const inBand = (t) => {
      const r = t.getBoundingClientRect();
      return r.top >= innerHeight * BAND && r.bottom <= innerHeight * (1 - BAND);
    };
    // midScroll: only cards wholly inside the middle band (they stay in view for the whole peek); at rest: any card ≥60% on screen.
    const tryPeek = (midScroll) => {
      if (shopperSwiped || document.hidden) return;
      const wait = gap - (performance.now() - lastAt);
      if (wait > 0) {
        if (!midScroll) rest = setTimeout(() => tryPeek(false), wait); // at rest: wait out the gap unless the shopper scrolls on
        return;
      }
      const pool = [...onScreen].filter((t) => eligible(t) && (!midScroll || inBand(t)));
      while (pool.length) {
        const t = pool.splice(Math.floor(Math.random() * pool.length), 1)[0];
        const r = t.getBoundingClientRect();
        const top = document.elementFromPoint(r.left + r.width / 2, r.top + r.height / 2);
        if (!top || !t.contains(top)) continue; // covered: try another card
        peeked.add(t);
        lastAt = performance.now();
        gap = nextGap();
        t.classList.add(HINT);
        setTimeout(() => t.classList.remove(HINT), 1600);
        return;
      }
    };
    // Page speed from consecutive scroll events (scrollY only: no layout), smoothed so one jumpy frame doesn't decide it.
    let lastY = scrollY;
    let lastT = performance.now();
    let speed = Infinity; // unknown until two scroll events in a row
    let lastCheck = 0;
    addEventListener('scroll', () => {
      const now = performance.now();
      const dt = Math.max(1, now - lastT);
      const inst = Math.abs(scrollY - lastY) / dt;
      // The first event after a pause has a long dt and reads as slow even at the start of a flick: speed is unknown
      // until the next event.
      if (dt > 150) speed = Infinity;
      else speed = speed === Infinity ? inst : speed * 0.6 + inst * 0.4;
      lastY = scrollY;
      lastT = now;
      clearTimeout(rest);
      rest = setTimeout(() => tryPeek(false), 350);
      if (now - lastCheck >= 200 && speed <= SLOW) {
        lastCheck = now;
        tryPeek(true);
      }
    }, { passive: true });
  }
  randomPeeks();

  new MutationObserver((records) => {
    if (hintCheck) hintCheck();
    for (const record of records) {
      for (const node of record.addedNodes) {
        if (node.nodeType !== 1) continue;
        watch(node);
        if (peekObserve) peekObserve(node);
      }
    }
  }).observe(document.documentElement, { childList: true, subtree: true });
})();
